<template>
  <div
    class="media-blocks critical-component-clear"
    :style="{
      '--section-spacing': spacing,
      '--section-spacing-l': spacingDesktop
    }"
  >
    <component
      :is="getBlockComponent(block)"
      v-for="(block, index) of blocks"
      ref="block"
      :key="`media-blocks-${index}`"
      :href="block.url"
      class="media-blocks__block"
      :class="{ video: block.video }"
    >
      <video-player
        v-if="block.video"
        block-class="media-blocks"
        :src="`${block.video}#t=0.001`"
        overlay
        :autoplay="autoplayVideo(block)"
        :loop="autoplayVideo(block)"
        :muted="autoplayVideo(block)"
        :poster="block.image && block.image.src"
        :preload="videoPreloadValue(autoplayOverride)"
        :controls="isMobile ? block.mobile_controls : block.desktop_controls"
      />

      <template v-else-if="block.image">
        <picture class="media-blocks__picture">
          <template v-if="block.mobile_image">
            <source
              media="(max-width: 1023px)"
              :srcset="block.mobile_image.src"
              sizes="100vw"
              fetchpriority="high"
            />
            <source
              media="(min-width: 1024px)"
              :srcset="block.image.src"
              sizes="100vw"
            />
            <img
              :src="block.mobile_image.src"
              :alt="block.mobile_image.alt"
              class="images__image"
              :class="$blockClass(blockClass, 'image')"
              :loading="block.lazy_load ? 'lazy' : null"
              decoding="async"
              sizes="100vw"
              fetchpriority="high"
              :style="{
                'object-position': isMobile ? (block.mobile_image.presentation?.focal_point || 'center') : (block.image.presentation?.focal_point || 'center'),
                'width': block.mobile_image.width,
                'height': block.mobile_image.height
              }"
            />
          </template>
          <responsive-image
            v-else
            block-class="media-blocks"
            :image="block.image"
            :lazyload="block.lazy_load"
            :loaded-animation="false"
            min-max="2048-4096"
            :padding="false"
            :ratio="false"
            sizes="100vw"
          />
        </picture>
      </template>

      <div
        v-if="!block.video && blockHasOverlay(block)"
        class="media-blocks__overlay"
      >
        <span
          v-if="block.subtitle"
          class="
            media-blocks__subtitle
            text-body-xs-label
            text-body-s-label-desktop
          "
          v-text="block.subtitle"
        />

        <component
          :is="liquid.sectionIndex === 0 && index === 0 ? 'h1' : 'p'"
          v-if="block.title"
          class="media-blocks__title text-heading-l text-heading-2xl-desktop"
        >
        <span v-text="block.title" />
        </component>

        <div
          v-if="block.content"
          class="media-blocks__content"
        >
          <p
            class="media-blocks__content-text text-body-s text-body-m-desktop"
            v-text="block.content"
          />
        </div>

        <btn
          v-if="block.url && block.button"
          :label="block.button"
          block-class="media-blocks"
          parent-hover
        />
      </div>
    </component>
  </div>
</template>

<script>

/**
 * Vue: Media blocks (media-blocks)
 * -----------------------------------------------------------------------------
 * Full bleed image or video blocks.
 *
 * @param {Object[]} blocks - The list of media blocks.
 * @param {String} blocks[].video - Optional video source, disables content.
 * @param {Object} blocks[].image - The image object.
 * @param {String} blocks[].subtitle - Short text displayed above the title.
 * @param {String} blocks[].title - The block title.
 * @param {String} blocks[].content - Longer content displayed below the title.
 * @param {String} blocks[].button - The button label, hidden if no link is
 * provided.
 * @param {Boolean} blocks[].lazyLoad - Lazy load the image.
 * @param {Boolean} blocks[].autoplay - Auto Play the video.
 * @param {String} blocks[].url - The block link, disabled if video is active.
 * @param {String} spacing - Custom spacing value.
 * @param {String} spacingDesktop - Custom spacing value for desktop.
 * @param {Number} liquid.sectionIndex - Section's 0 based index.
 * @param {Boolean|Null} autoplayOverride - Overrides the block level
 * autoplay and autoplays all videos in the section.
 *
 */
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~async/video-player/video-player'

import { setIntersectionObserver, videoPreloadValue } from '~/helpers/general'
import classes from '~/config/classes'
import { mapState } from 'vuex'

const transitions = {
  block: {
    in: 1,
    out: 0.8,
  },
}

export default {
  name: 'MediaBlocks',

  components: {
    Btn,
    ResponsiveImage,
    VideoPlayer,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },

    spacing: {
      type: String,
      default: '',
    },

    spacingDesktop: {
      type: String,
      default: '',
    },

    autoplayOverride: {
      type: [Boolean, null],
      default: null,
    },

    liquid: {
      type: Object,
      default: () => ({
        sectionIndex: 0,
      }),
    },
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.$refs.block?.forEach((element) => {
      setIntersectionObserver({
        callback: this.handleBlockIntersection,
        element,
        threshold: [transitions.block.in, transitions.block.out]
      })
    })
  },

  methods: {
    videoPreloadValue,

    /**
     * Handles a block's intersection event.
     * @param {Array} entries - A list of intersection events.
     * @param {Object} observer - The observer instance.
     */
     handleBlockIntersection(entries) {
      entries.forEach((entry) => {
        if (
          !entry.isIntersecting &&
          entry.intersectionRatio <= transitions.block.out
        ) {
          entry.target.classList.remove(classes.active)
          return
        }

        entry.target.classList.add(classes.active)
      })
    },

    /**
     * Returns the block component type.
     * @param {Object} block - The block object.
     * @returns {String}
     */
    getBlockComponent(block) {
      if (!block.url || block.video) {
        return 'div'
      }

      return 'a'
    },

    /**
     * Returns if the given block has an overlay.
     * @param {Object} block - The block object.
     * @returns {Boolean}
     */
    blockHasOverlay(block) {
      return block.subtitle ||
        block.title ||
        block.content ||
        (block.url && block.button)
    },

    /**
     * Determines whether the video should be auto-played based on
     * the block settings and if there is a section autoplay override
     * in place.
     * @param {Object} block - Block data.
     * @returns {Boolean}
     */
    autoplayVideo(block) {
      if (this.autoplayOverride !== null) {
        return this.autoplayOverride
      }

      if (block.autoplay) {
        return block.autoplay
      }

      return false
    },
  },
}
</script>
<style lang="scss">
@import './media-blocks';
</style>
